import { useEffect, useState } from 'react'
import { DeviceIndexType } from './deviceDetail.types'

/**
 * 딕셔너리 런타임 색인 — `/works-with` 와 `/products/pls8` 이 같이 쓴다 (SITEMAP_PLAN §22.14 · §29.3).
 *
 * 정본(S3 딕셔너리)이 바뀌면 `npm run deploy:devices` 가 `public/works-with/*.json` 만 갈아 끼운다.
 * 리액트 빌드도 프리렌더도 없다. 그래서 번들의 `devices.generated.ts` 는 마지막 **빌드** 시점이고
 * 이 색인은 마지막 **동기화** 시점이다. 화면은 번들 사본으로 먼저 그리고(프리렌더 HTML·첫 페인트에
 * 빈 값이 없다) 색인이 도착하면 갈아 끼운다. 깨진 응답은 조용히 버린다.
 */
const INDEX_URL = `${process.env.PUBLIC_URL || ''}/works-with/index.json`

/**
 * 받아 둔 색인. 탭을 닫을 때까지 산다. 모듈 스코프라 페이지를 여러 번 드나들어도, 두 페이지가
 * 번갈아 불러도 한 번만 받는다. 딕셔너리는 `deploy:devices` 가 밀어 넣을 때만 바뀌고 그때
 * CloudFront 무효화가 함께 돌기 때문에 한 세션 안에서 값이 바뀌는 일은 사실상 없다.
 */
let cachedIndex: DeviceIndexType | null = null
/** 같은 화면에서 두 번 부를 때 요청이 겹치지 않게 붙잡아 둔다 */
let indexInFlight: Promise<DeviceIndexType | null> | null = null

const loadIndex = (): Promise<DeviceIndexType | null> => {
	if (cachedIndex) return Promise.resolve(cachedIndex)
	if (indexInFlight) return indexInFlight

	indexInFlight = fetch(INDEX_URL)
		.then(res => (res.ok ? res.json() : null))
		.then((data: DeviceIndexType | null) => {
			if (!data || !data.stats || !Array.isArray(data.list) || data.list.length === 0)
				return null
			cachedIndex = data
			return data
		})
		.catch(() => null)
		.finally(() => {
			indexInFlight = null
		})

	return indexInFlight
}

/** 최신 색인. 아직 없거나 못 받았으면 null — 호출하는 쪽이 번들 사본(`deviceStats`·`deviceList`)으로 대신한다. */
export const useDeviceIndex = (): DeviceIndexType | null => {
	const [index, setIndex] = useState<DeviceIndexType | null>(cachedIndex)

	useEffect(() => {
		if (cachedIndex) return
		let alive = true
		/* loadIndex 는 스스로 catch 한다. 실패하면 번들 사본으로 남는다 */
		void loadIndex().then(data => {
			if (alive && data) setIndex(data)
		})
		return () => {
			alive = false
		}
	}, [])

	return index
}
